import { Box, FormControlLabel, FormHelperText, Radio, RadioGroup, styled, ThemeProvider } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PayPal from "./account/images/PayPal_2022.svg";
import Venmo from "./account/images/Blue.webp";
import { Context } from "../common/context";
import { createCustomTheme } from "./account/theme";
import { Axios, enterInput, formatPhone } from "../common/utils";
import { baseApi } from "../common/constants";
import { accountInputBoxCSS, AppLoadingButton, LoginButton, SignInCard, SignInContainer } from "./subComps/loginPieces";
import { fieldRules, LiLink } from "../common/commonComps";
import { AccountInputBox } from "./account/components/TableActions";
import Ad from "./ad";
import type { Size, AppContext } from "../types";

const SignUpBox = styled(Box)(() => ({
    display: `flex`,
    justifyContent: `space-around`,
    alignItems: `center`,
    padding: `20px 0`,
}));

const Login = ({ size, context }: { size: Size; context: AppContext }) => {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [phase, setPhase] = useState(0);
    const [paypal, setPaypal] = useState(`paypal`);
    const [email, setEmail] = useState(localStorage.getItem(`signupemail`) || ``);
    const [phone, setPhone] = useState(``);
    const [bc] = useState(new BroadcastChannel(`li_login`));
    const location = useLocation();
    const navigate = useNavigate();
    const isIframe = useMemo(() => window.self !== window.top, []);

    useEffect(() => {
        localStorage.removeItem(`signupemail`);
        bc.onmessage = m => {
            try {
                const result = JSON.parse(m.data);
                setPhase(result.phase);
                if (window.location.pathname !== result.pathname) {
                    navigate(result.pathname);
                }
            } catch (e) {
                (() => {})();
            }
        };
    }, []);

    useEffect(() => {
        bc.postMessage(JSON.stringify({ phase, pathname: location.pathname }));
        if (isIframe) {
            window.parent.postMessage({ resp: phase, action: `onboarding` }, `*`);
        }
    }, [location.pathname, phase]);

    const send = () => {
        if (phase === 2) {
            bc.postMessage(JSON.stringify({ phase, pathname: `/` }));
            window.parent.postMessage({ resp: 3, action: `onboarding` }, `*`);
            navigate(`/`);
            return;
        }
        if (phase === 1) {
            return setPhase(2);
        }
        fieldRules[paypal].validator
            .validate({
                [paypal === `paypal` ? `paypal-email` : `venmo-phone`]: fieldRules[paypal].fields[0].regex
                    ? phone.replace(fieldRules[paypal].fields[0].regex, ``)
                    : email,
            })
            .then(() => {
                setError(null);
                setLoading(true);
                return Axios.put(`${baseApi}usersetting`, {
                    programGuid: context.programGuid,
                    mallUserSetting: [
                        {
                            name: paypal === `paypal` ? `paypal-email` : `venmo-phone`,
                            value: paypal === `paypal` ? email : phone,
                            category: `mall-payments`,
                        },
                        { name: `paypal`, value: paypal === `paypal`, category: `mall-payments` },
                    ],
                });
            })
            .then(() => {
                setLoading(false);
                if (context.liBxInstalledData) {
                    navigate(`/`);
                }
                setPhase(1);
            })
            .catch(e => {
                setError(e?.errors?.[0] || `Sorry, something went wrong. Please try again.`);
                setLoading(false);
            });
    };

    return (
        <Context.Consumer>
            {value =>
                !value.context?.adminConfig ? null : (
                    <ThemeProvider theme={createCustomTheme({ context: value.context })}>
                        <SignInContainer size={size} direction="column" justifyContent="space-between">
                            <Box
                                sx={{
                                    margin: `0 auto ${size === `small` ? 0 : `24px`}`,
                                }}
                            >
                                <Box
                                    sx={{ height: `auto`, width: `200px` }}
                                    component="img"
                                    src={value.context?.adminConfig?.mall?.Color_Desktop_Logo}
                                />
                            </Box>
                            {!value.context?.adminConfig?.mall?.enable_login_page && (
                                <Typography
                                    component="h1"
                                    variant="h4"
                                    sx={{
                                        fontFamily: `Plus Jakarta Sans`,
                                        width: `100%`,
                                        fontSize: `36px`,
                                        fontWeight: 700,
                                        textAlign: `center`,
                                    }}
                                >
                                    This function is disabled
                                </Typography>
                            )}
                            {value.context?.adminConfig?.mall?.enable_login_page && (
                                <SignInCard size={size} lastPage={phase === 2} wide={phase === 0} variant="outlined">
                                    {phase === 2 && (
                                        <Typography
                                            component="h1"
                                            variant="h4"
                                            sx={{
                                                fontFamily: `Plus Jakarta Sans`,
                                                width: `100%`,
                                                fontSize: `36px`,
                                                fontWeight: 700,
                                                marginBottom: `16px`,
                                                textAlign: `center`,
                                            }}
                                        >
                                            You&apos;re Ready To Go
                                        </Typography>
                                    )}
                                    {phase === 0 && (
                                        <>
                                            <Typography
                                                component="h1"
                                                variant="h4"
                                                sx={{
                                                    fontFamily: `Plus Jakarta Sans`,
                                                    width: `100%`,
                                                    fontSize: `36px`,
                                                    fontWeight: 700,
                                                    marginBottom: `8px`,
                                                    textAlign: `center`,
                                                }}
                                            >
                                                {phase === 0 && `How Do You Want To Get Paid?`}
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontSize: `1rem`,
                                                    fontWeight: 400,
                                                    textAlign: `center`,
                                                    marginBottom: `24px`,
                                                }}
                                            >
                                                {phase === 0 && `We'll send your cash back to the account you select.`}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: `flex`,
                                                    flexDirection: `column`,
                                                    width: `100%`,
                                                    gap: 1,
                                                }}
                                            >
                                                <FormControl>
                                                    <RadioGroup
                                                        sx={{ display: `block`, mb: 2 }}
                                                        onChange={v => {
                                                            setError(``);
                                                            setPaypal(v.target.value);
                                                        }}
                                                        aria-labelledby="radio-buttons-group-label"
                                                        value={paypal}
                                                        name="radio-buttons-group"
                                                    >
                                                        <FormControlLabel
                                                            sx={{ marginRight: 0, verticalAlign: `middle` }}
                                                            value="paypal"
                                                            control={<Radio />}
                                                            label={
                                                                <Box
                                                                    sx={{
                                                                        height: 25,
                                                                        verticalAlign: `middle`,
                                                                        cursor: `pointer`,
                                                                    }}
                                                                    component="img"
                                                                    src={PayPal}
                                                                />
                                                            }
                                                        />
                                                        <FormControlLabel
                                                            sx={{ marginRight: 0, verticalAlign: `middle` }}
                                                            value="venmo"
                                                            control={<Radio />}
                                                            label={
                                                                <Box
                                                                    sx={{
                                                                        height: 15,
                                                                        verticalAlign: `middle`,
                                                                        cursor: `pointer`,
                                                                    }}
                                                                    component="img"
                                                                    src={Venmo}
                                                                />
                                                            }
                                                        />
                                                    </RadioGroup>
                                                    <AccountInputBox
                                                        onClick={() =>
                                                            paypal === `paypal` ? setEmail(``) : setPhone(``)
                                                        }
                                                        id={paypal === `paypal` ? `email` : `phone`}
                                                        type={paypal === `paypal` ? `email` : `phone`}
                                                        name={paypal === `paypal` ? `email` : `phone`}
                                                        label={paypal === `paypal` ? `Email` : `Phone`}
                                                        autoComplete={paypal === `paypal` ? `email` : `phone`}
                                                        onKeyDown={e => enterInput(e, send)}
                                                        onChange={v =>
                                                            paypal === `paypal`
                                                                ? setEmail(v.target.value)
                                                                : setPhone(formatPhone(v.target.value))
                                                        }
                                                        fullWidth
                                                        variant="outlined"
                                                        InputProps={{
                                                            sx: {
                                                                background: `#FFF`,
                                                                marginTop: `5px`,
                                                            },
                                                        }}
                                                        sx={{
                                                            ...accountInputBoxCSS,
                                                            marginBottom: 3,
                                                            "& label": {
                                                                top: 0,
                                                                "&.MuiInputLabel-shrink": {
                                                                    top: `6px`,
                                                                },
                                                            },

                                                            maxWidth: `unset`,
                                                            input: { fontWeight: 500 },
                                                        }}
                                                        currentValue={paypal === `paypal` ? email : phone}
                                                    />
                                                </FormControl>

                                                {error && (
                                                    <FormHelperText
                                                        error
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 500,
                                                            minHeight: `22px`,
                                                            minWidth: `2px`,
                                                            textAlign: `center`,
                                                        }}
                                                    >
                                                        {error}
                                                    </FormHelperText>
                                                )}
                                            </Box>
                                        </>
                                    )}
                                    {phase === 1 &&
                                        context?.adminConfig?.mall?.ad_placements?.register_placement &&
                                        !context.liBxInstalledData && (
                                            <Box
                                                sx={{
                                                    display: `flex`,
                                                    alignItems: `center`,
                                                    justifyContent: `center`,
                                                }}
                                            >
                                                <LiLink
                                                    to={
                                                        context?.adminConfig?.mall?.ad_placements?.register_placement
                                                            .url || `#`
                                                    }
                                                >
                                                    <Ad
                                                        {...{
                                                            ...context?.adminConfig?.mall?.ad_placements
                                                                ?.register_placement,
                                                            size,
                                                            sx: { px: 0 },
                                                        }}
                                                    />
                                                </LiLink>
                                            </Box>
                                        )}
                                    {!loading && (
                                        <LoginButton
                                            sx={{
                                                background: `linear-gradient(1deg, #007238, #00a651)`,
                                                borderRadius: `50px`,
                                                width: `100%`,
                                                margin: `0 auto`,
                                                ...(phase === 1 ? { background: `none`, color: `#999` } : {}),
                                            }}
                                            variant={phase === 1 ? `text` : `contained`}
                                            onClick={send}
                                        >
                                            {phase === 1 ? `SKIP` : phase === 0 ? `NEXT` : `Start Shopping`}
                                        </LoginButton>
                                    )}
                                    {loading && (
                                        <AppLoadingButton
                                            sx={{
                                                width: `100%`,
                                                margin: `0 auto`,
                                                ...(phase === 1 ? { background: `none`, color: `#999` } : {}),
                                            }}
                                            loading
                                            variant={phase === 1 ? `text` : `contained`}
                                        >
                                            {phase === 1 ? `SKIP` : `NEXT`}
                                        </AppLoadingButton>
                                    )}
                                    {phase === 0 && (
                                        <SignUpBox>
                                            <LiLink
                                                target="_blank"
                                                rel="nopeneer noreferrer"
                                                to="https://www.paypal.com/us/webapps/mpp/account-selection"
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        mt: 0,
                                                        fontSize: 13,
                                                        fontWeight: 500,
                                                        minHeight: `22px`,
                                                        minWidth: `2px`,
                                                        color: `#666`,
                                                    }}
                                                >
                                                    Create PayPal Account
                                                </Typography>
                                            </LiLink>
                                            <LiLink
                                                target="_blank"
                                                rel="nopeneer noreferrer"
                                                sx={{
                                                    color: `#999`,
                                                    fontSize: 13,
                                                    "&:visited": {
                                                        color: `#999`,
                                                    },
                                                }}
                                                to="https://venmo.com/signup/"
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        mt: 0,
                                                        fontSize: 13,
                                                        fontWeight: 500,
                                                        minHeight: `22px`,
                                                        minWidth: `2px`,
                                                        color: `#666`,
                                                    }}
                                                >
                                                    Create Venmo Account
                                                </Typography>
                                            </LiLink>
                                        </SignUpBox>
                                    )}
                                </SignInCard>
                            )}
                        </SignInContainer>
                    </ThemeProvider>
                )
            }
        </Context.Consumer>
    );
};

export default Login;
